import {useCallback, useContext, useEffect, useRef, useState} from "react";
import {ToastContext} from "Toast";
import {useMessages} from "i18n";
import {useIsLoggedIn, useUser} from "UserProvider";
import {
	type FeedbackTopic,
	maxMessageLengthForTopic,
	tagMessageWithTopic,
} from "core/support/feedbackTopic";
import {SingleFlight} from "../utils/SingleFlight";
import {FeedbackApi} from "./FeedbackApi";
import {
	MAX_FEEDBACK_MESSAGE_LENGTH,
	type FeedbackFieldErrors,
	NO_FEEDBACK_FIELD_ERRORS,
	hasFeedbackFieldError,
	isFeedbackDraftComplete,
	normalizeReplyTo,
	validateFeedbackDraft,
} from "./feedbackValidation";

/**
 * The behaviour contract of the Support/Feedback dialog (epic #26): all state + the submit action,
 * so the view (`Feedback.tsx`) is a dumb, restyleable shell. The presentation reads these fields and
 * renders the idle form, the in-flight state, or the success moment — it owns no business logic.
 */
export interface FeedbackController {
	/** The note the user is writing. */
	message: string;
	setMessage: (value: string) => void;
	/**
	 * What the note is about, or `null` for "not stated" — the initial value, and a valid final one.
	 * The topic is never required: an unanswered question must not block a note from being sent.
	 */
	topic: FeedbackTopic | null;
	setTopic: (value: FeedbackTopic | null) => void;
	/**
	 * How many characters the sender may actually type, once the chosen topic's inbox tag is deducted
	 * from the api's bound. The view drives its counter, its `maxLength` and its error text from THIS
	 * number rather than the raw constant, so all three agree with what the api will measure.
	 */
	maxMessageLength: number;
	/** The optional reply-to address (prefilled for a logged-in user, always editable). */
	email: string;
	setEmail: (value: string) => void;
	/**
	 * True while a send is in flight. Drives the spinner and the Send button's `aria-disabled`; the
	 * button is never DISABLED for it, because a disabled button drops the focus it holds out of the
	 * dialog (kitecorp/transferify-ui#3079). A press meanwhile joins the send already in flight.
	 */
	submitting: boolean;
	/** True after a successful send — the view swaps to the calm "Thank you" moment. */
	submitted: boolean;
	/** True when the last send failed IN TRANSPORT; the typed message is kept so the user can retry. */
	error: boolean;
	/** Per-field verdict from the last submit — an error KEY per field, or null (ui#326). */
	fieldErrors: FeedbackFieldErrors;
	/**
	 * Whether the note has text, which is what arms the Send button. It stays true while the note's own
	 * send is in flight; {@link submitting} reports that. The rule is {@link isFeedbackDraftComplete}.
	 */
	draftComplete: boolean;
	/** Validate + POST the note, then toast and flip to the success moment. */
	submit: () => Promise<void>;
	/** Clear the success/error state so a re-opened dialog shows a fresh form. */
	reset: () => void;
}

/**
 * Support/Feedback state machine — a thin React adapter over {@link validateFeedbackDraft} and the
 * single {@link FeedbackApi.send} call; it holds no rules of its own. Prefills the reply-to from the
 * signed-in user (hydration-safe via {@link useIsLoggedIn} — never during the SSG/first render), while
 * leaving it fully editable.
 *
 * Two guarantees are deliberate:
 *
 * - **Nothing the sender can fix reaches the toast.** Every rejection `POST /feedback` can raise is
 *   mirrored client-side and reported per FIELD, because as one opaque toast a 400 on the message
 *   length or the reply-to grammar was indistinguishable from a network blip — and retrying it could
 *   never succeed (ui#326).
 * - **`draftComplete` never falls to a fixable error.** Disabling the button on an invalid draft would
 *   reproduce exactly that dead end from the other side: the sender would be blocked with no way to
 *   learn why. The button stays live and the submit explains.
 */
export function useFeedback(): FeedbackController {
	const m = useMessages();
	const {showErrorToast, showSuccessToast} = useContext(ToastContext);
	const [user] = useUser();
	const isLoggedIn = useIsLoggedIn();

	const [message, setMessageState] = useState("");
	const [topic, setTopicState] = useState<FeedbackTopic | null>(null);
	const [email, setEmailState] = useState("");
	const [submitting, setSubmitting] = useState(false);
	const [submitted, setSubmitted] = useState(false);
	const [error, setError] = useState(false);
	const [fieldErrors, setFieldErrors] = useState<FeedbackFieldErrors>(NO_FEEDBACK_FIELD_ERRORS);

	// Once the user edits the address we stop auto-filling it (so we never clobber their choice).
	const emailTouched = useRef(false);
	// Synchronous admission gate: the active send is published before `submit` returns, so a hammered
	// button joins the ONE request in flight rather than waiting for `submitting` to reach the DOM.
	const submitFlight = useRef<SingleFlight | null>(null);
	submitFlight.current ??= new SingleFlight();

	// Prefill the reply-to from the account, but only after mount (a logged-in visitor is logged-out
	// during the pre-render / first client render) and only until the field is touched.
	useEffect(() => {
		if (!emailTouched.current && isLoggedIn && user.email) {
			setEmailState(user.email);
		}
	}, [isLoggedIn, user.email]);

	// Editing a field retracts its complaint: an error the sender is actively fixing is noise, and a
	// stale one next to corrected input reads as "still wrong".
	const setMessage = useCallback((value: string) => {
		setMessageState(value);
		setFieldErrors(current => (current.message === null ? current : {...current, message: null}));
	}, []);

	const setEmail = useCallback((value: string) => {
		emailTouched.current = true;
		setEmailState(value);
		setFieldErrors(current => (current.replyTo === null ? current : {...current, replyTo: null}));
	}, []);

	// Retracts the MESSAGE's complaint, not one of its own — the topic is the only control that can
	// change the message's verdict without the message being touched, because it moves the budget the
	// length was judged against. Leaving the old verdict up would state a limit the field no longer has.
	const setTopic = useCallback((value: FeedbackTopic | null) => {
		setTopicState(value);
		setFieldErrors(current => (current.message === null ? current : {...current, message: null}));
	}, []);

	const draftComplete = isFeedbackDraftComplete({message});
	const maxMessageLength = maxMessageLengthForTopic(topic, MAX_FEEDBACK_MESSAGE_LENGTH);

	const submit = useCallback((): Promise<void> => submitFlight.current!.run(async () => {
		const replyTo = normalizeReplyTo(email);
		// The TYPED message against the budget the topic leaves — equivalent to measuring the tagged
		// body against the api's bound, but it reports the length the sender can see and act on.
		const verdict = validateFeedbackDraft({message, replyTo}, maxMessageLength);
		setFieldErrors(verdict);
		if (hasFeedbackFieldError(verdict)) {
			return;
		}
		setSubmitting(true);
		setError(false);
		try {
			// The message is posted untrimmed (the api measures the raw string) with the topic's inbox
			// tag prepended — and byte-identical to before when no topic was chosen; the address is
			// posted normalized, because the api reads "" as absent but rejects a whitespace-only value.
			await FeedbackApi.send({message: tagMessageWithTopic(topic, message), replyTo});
			setMessageState("");
			// Cleared with the note it described: a second note inheriting the first one's topic would
			// mislabel it, and the sender has no reason to expect the control to still be set. The RAW
			// setter, because this is the form emptying itself rather than the sender editing a field.
			setTopicState(null);
			setSubmitted(true);
			showSuccessToast(m.feedback.toasts.sent);
		} catch {
			setError(true);
			showErrorToast(m.feedback.errors.sendFailed);
		} finally {
			setSubmitting(false);
		}
	}), [message, topic, maxMessageLength, email, m, showErrorToast, showSuccessToast]);

	const reset = useCallback(() => {
		setSubmitted(false);
		setError(false);
		setFieldErrors(NO_FEEDBACK_FIELD_ERRORS);
	}, []);

	return {
		message, setMessage, topic, setTopic, maxMessageLength, email, setEmail,
		submitting, submitted, error, fieldErrors, draftComplete, submit, reset,
	};
}
